import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu';
import { Search, Zap, Send, Eye, XCircle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getLoadStatusClasses } from '@/lib/statusColors';

const SERVICE_STYLES = {
  regular: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  rush: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  direct: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

function parseAddress(address) {
  if (!address) return { street: '', city: '' };
  const parts = address.split(',').map(p => p.trim());
  return { street: parts[0] || '', city: parts[1] || '' };
}

export default function PendingLoadsGrid({
  pendingLoads, pendingTab, setPendingTab,
  selectedLoadIds, toggleLoadSelection, selectAllLoads,
  pendingSearch, setPendingSearch,
  onScore, onAutoDispatch,
}) {
  const allSelected = pendingLoads.length > 0 && pendingLoads.every(l => selectedLoadIds.has(l.load_id));

  const handleSelectAll = () => {
    if (allSelected) selectAllLoads([]);
    else selectAllLoads(pendingLoads.map(l => l.load_id));
  };

  const selectedInGrid = pendingLoads.filter(l => selectedLoadIds.has(l.load_id)).length;

  return (
    <div className="flex flex-col h-full">
      <Tabs value={pendingTab} onValueChange={setPendingTab} className="flex flex-col h-full">
        <div className="flex items-center justify-between px-3 pt-2 pb-1 border-b border-border bg-surface/30">
          <TabsList className="bg-muted/50 h-7">
            <TabsTrigger value="ready" className="text-[10px] h-6 px-3 data-[state=active]:bg-brand-accent data-[state=active]:text-brand-accent-foreground">
              Ready to Ship
            </TabsTrigger>
            <TabsTrigger value="quotes" className="text-[10px] h-6 px-3 data-[state=active]:bg-brand-accent data-[state=active]:text-brand-accent-foreground">
              Quotes
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="text-[10px] h-6 px-3 data-[state=active]:bg-brand-accent data-[state=active]:text-brand-accent-foreground">
              Scheduled Loads
            </TabsTrigger>
            <TabsTrigger value="cancelled" className="text-[10px] h-6 px-3 data-[state=active]:bg-brand-accent data-[state=active]:text-brand-accent-foreground">
              Cancelled
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-3">
            <span className="text-[10px] text-muted-foreground">
              Total Loads: {pendingLoads.length} AND Selected Loads: {selectedInGrid}
            </span>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={pendingSearch}
                onChange={e => setPendingSearch(e.target.value)}
                className="bg-muted border-input text-foreground h-7 text-xs pl-7 w-48"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <TabsContent value={pendingTab} className="h-full mt-0">
            <ScrollArea className="h-full">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="w-8 px-2">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                        className="border-input data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent"
                      />
                    </TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Load Number</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Service</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Vehicle</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Status</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Customer Name</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Shipper Name</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Shipper Street</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Shipper City</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Consignee Name</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2">Consignee Street</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2 text-right">Weight</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2 text-right">Distance</TableHead>
                    <TableHead className="text-[10px] text-muted-foreground font-semibold px-2 text-right">Subtotal</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingLoads.map(load => {
                    const pickup = parseAddress(load.pickup_address);
                    const delivery = parseAddress(load.delivery_address);
                    const isSelected = selectedLoadIds.has(load.load_id);
                    return (
                      <ContextMenu key={load.load_id}>
                        <ContextMenuTrigger asChild>
                          <TableRow
                            className={`border-border/50 cursor-pointer transition-colors ${
                              isSelected ? 'bg-brand-accent/5' : 'hover:bg-muted/30'
                            }`}
                            onClick={() => toggleLoadSelection(load.load_id)}
                          >
                            <TableCell className="px-2">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleLoadSelection(load.load_id)}
                                className="border-input data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent"
                                onClick={e => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell className="px-2">
                              <span className="text-[11px] font-medium text-brand-accent">
                                {load.load_id?.slice(-6)}
                              </span>
                            </TableCell>
                            <TableCell className="px-2">
                              <Badge className={`text-[9px] px-1.5 py-0 ${SERVICE_STYLES[load.delivery_type] || SERVICE_STYLES.regular}`}>
                                {(load.delivery_type || 'regular').charAt(0).toUpperCase() + (load.delivery_type || 'regular').slice(1)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-[11px] text-muted-foreground px-2">
                              {load.equipment_type_required || '--'}
                            </TableCell>
                            <TableCell className="px-2">
                              <Badge className={`text-[9px] px-1.5 py-0 ${getLoadStatusClasses(load.status)}`}>
                                {load.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-[11px] text-muted-foreground px-2 max-w-[120px] truncate">
                              {load.client_name || '--'}
                            </TableCell>
                            <TableCell className="text-[11px] text-muted-foreground px-2 max-w-[120px] truncate">
                              {load.pickup_name || '--'}
                            </TableCell>
                            <TableCell className="text-[11px] text-muted-foreground px-2 max-w-[140px] truncate">
                              {pickup.street}
                            </TableCell>
                            <TableCell className="text-[11px] text-muted-foreground px-2">
                              {pickup.city}
                            </TableCell>
                            <TableCell className="text-[11px] text-muted-foreground px-2 max-w-[120px] truncate">
                              {load.delivery_name || '--'}
                            </TableCell>
                            <TableCell className="text-[11px] text-muted-foreground px-2 max-w-[140px] truncate">
                              {delivery.street}
                            </TableCell>
                            <TableCell className="text-[11px] text-muted-foreground px-2 text-right">
                              {load.weight_lbs ? `${load.weight_lbs.toLocaleString()} lbs` : '--'}
                            </TableCell>
                            <TableCell className="text-[11px] text-muted-foreground px-2 text-right">
                              {load.distance_miles ? `${Math.round(load.distance_miles * 1.60934)} km` : '--'}
                            </TableCell>
                            <TableCell className="text-[11px] text-foreground font-medium px-2 text-right">
                              {load.rate ? `$${Number(load.rate).toFixed(2)}` : '--'}
                            </TableCell>
                          </TableRow>
                        </ContextMenuTrigger>
                        <ContextMenuContent className="bg-muted border-input text-foreground">
                          <ContextMenuItem
                            className="text-xs gap-2 focus:bg-input"
                            onClick={() => onScore(load.load_id)}
                          >
                            <Zap className="w-3 h-3" /> Score Drivers
                          </ContextMenuItem>
                          <ContextMenuItem
                            className="text-xs gap-2 focus:bg-input"
                            onClick={() => onAutoDispatch(load.load_id)}
                          >
                            <Send className="w-3 h-3" /> Auto-Dispatch
                          </ContextMenuItem>
                        </ContextMenuContent>
                      </ContextMenu>
                    );
                  })}
                  {pendingLoads.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center py-8 text-muted-foreground text-xs">
                        No loads in this category
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
